import { useState, useEffect, useCallback } from 'react';
import { Activity, Mail, RefreshCw } from 'lucide-react';
import { api } from '../../lib/api';

function timeAgo(iso) {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function webhookStatus(iso) {
  if (!iso) return { color: 'bg-gray-500', text: 'text-gray-400', label: 'No webhooks yet' };
  const hours = (Date.now() - new Date(iso).getTime()) / 3600000;
  if (hours < 24) return { color: 'bg-green-500', text: 'text-green-400', label: 'Healthy' };
  if (hours < 72) return { color: 'bg-yellow-500', text: 'text-yellow-400', label: 'Quiet' };
  return { color: 'bg-red-500', text: 'text-red-400', label: 'Stale' };
}

function emailStatus(email) {
  if (!email?.last_attempt_at) return { color: 'bg-gray-500', text: 'text-gray-400', label: 'No emails yet' };
  if (email.last_status === 'failure') return { color: 'bg-red-500', text: 'text-red-400', label: 'Failing' };
  return { color: 'bg-green-500', text: 'text-green-400', label: 'Healthy' };
}

function HealthRow({ icon: Icon, title, status, detail, testIdPrefix, pulse }) {
  return (
    <div className="flex items-center gap-3 flex-1 min-w-[260px]">
      <div className="w-10 h-10 bg-lime/10 rounded-xl flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-lime" />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${status.color} ${pulse ? 'animate-pulse' : ''}`} />
          <p className="text-sm font-medium">
            {title}
            <span className={`ml-2 text-xs ${status.text}`} data-testid={`${testIdPrefix}-status`}>{status.label}</span>
          </p>
        </div>
        <p className="text-xs text-[#666] truncate" data-testid={`${testIdPrefix}-last-received`}>{detail}</p>
      </div>
    </div>
  );
}

export function WebhookHealthWidget() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getAdminHealth();
      setHealth(res.data);
    } catch (e) {
      // silent — widget is non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 60000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const webhook = health?.stripe_webhook;
  const email = health?.resend_email;
  const whStatus = webhookStatus(webhook?.last_received_at);
  const emStatus = emailStatus(email);

  const webhookDetail = webhook?.last_received_at
    ? `Last received ${timeAgo(webhook.last_received_at)}${webhook.last_event_type ? ` • ${webhook.last_event_type}` : ''}`
    : 'No webhook events received yet';

  const emailDetail = email?.last_attempt_at
    ? email.last_status === 'failure'
      ? `Last attempt failed ${timeAgo(email.last_attempt_at)}${email.last_error ? ` • ${email.last_error}` : ''}`
      : `Last sent ${timeAgo(email.last_attempt_at)}`
    : 'No emails sent yet';

  return (
    <div className="bg-surface border border-[#262626] rounded-xl p-4 mb-8 flex items-center justify-between gap-4 flex-wrap" data-testid="webhook-health-widget">
      <div className="flex items-center gap-6 flex-wrap flex-1">
        <HealthRow
          icon={Activity}
          title="Stripe Webhook"
          status={whStatus}
          detail={webhookDetail}
          testIdPrefix="webhook-health"
          pulse={!!webhook?.last_received_at}
        />
        <HealthRow
          icon={Mail}
          title="Email Delivery"
          status={emStatus}
          detail={emailDetail}
          testIdPrefix="email-health"
          pulse={!!email?.last_attempt_at && email?.last_status !== 'failure'}
        />
      </div>
      <button
        onClick={fetchHealth}
        className="p-2 rounded-lg hover:bg-surface-highlight transition-colors text-[#A1A1AA] shrink-0"
        title="Refresh"
        data-testid="webhook-health-refresh"
      >
        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
}
